import { useState } from 'react';
import { useInterval } from 'usehooks-ts';

import Header from 'components/header';
import Button from 'components/button';
import Checkbox from 'components/checkbox';
import TextInput from 'components/text-input';
import PhoneInput from 'components/phone-input';
import BottomSheet from 'components/bottom-sheet';
import AuthCodeInput from 'components/auth-code-input';

import useBoundStore from 'store';
import { AUTH_CODE_RESEND_TIMEOUT } from 'lib/constants';
import { AuthBottomSheetState } from 'store/slices/ui';
import { signIn, updateUserInfo, verifyAuthCode } from 'api';

import userIcon from 'assets/images/user.svg';
import styles from './styles.module.scss';

const AuthBottomSheet: React.FC = () => {
  const isAuthBottomSheetOpen = useBoundStore((s) => s.isAuthBottomSheetOpen);
  const closeAuthBottomSheet = useBoundStore((s) => s.closeAuthBottomSheet);
  const authBottomSheetState = useBoundStore((s) => s.authBottomSheetState);
  const isAuth = useBoundStore.getState().authToken;

  const getHeader = () => {
    if (authBottomSheetState === AuthBottomSheetState.inputPhone) {
      return <Header text='Регистрация в системе ItReit' />;
    }
    if (authBottomSheetState === AuthBottomSheetState.inputCode) {
      return <Header text='Введите код активации' />;
    }
    if (authBottomSheetState === AuthBottomSheetState.inputName) {
      return <Header text='Напишите ваше имя' />;
    }
  };

  return (
    <BottomSheet
      header={getHeader()}
      open={isAuthBottomSheetOpen}
      onDismiss={isAuth ? closeAuthBottomSheet : undefined}
    >
      {authBottomSheetState === AuthBottomSheetState.inputPhone ? (
        <InputPhone />
      ) : authBottomSheetState === AuthBottomSheetState.inputCode ? (
        <InputCode />
      ) : authBottomSheetState === AuthBottomSheetState.inputName ? (
        <InputName />
      ) : (
        <></>
      )}
    </BottomSheet>
  );
};

const InputPhone: React.FC = () => {
  const proceedToAuthInputCode = useBoundStore((s) => s.proceedToAuthInputCode);

  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [personalDataConsent, setPersonalDataConsent] = useState(false);
  const [privacyPolicyConsent, setPrivacyPolicyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');

  const canProceed = phone.length === 15 && personalDataConsent && privacyPolicyConsent;

  const handleClick = async () => {
    setIsLoading(true);
    const { result, error } = await signIn({
      phone,
      personalDataConsent,
      privacyPolicyConsent,
      marketingConsent,
    });
    setIsLoading(false);

    if (result) proceedToAuthInputCode();
    else if (error) setError(error);
  };

  const setIsShowKeyBoardHeight = useBoundStore((s) => s.setIsShowKeyBoardHeight);

  return (
    <div className={styles.container}>
      <div>
        <p className={styles.desc}>
          Пожалуйста, укажите ваш номер телефона, на него будет отправлен код подтверждения
        </p>

        <PhoneInput
          autoFocus
          error={error}
          onChange={setPhone}
          className={styles.input}
          onFocus={() => setIsShowKeyBoardHeight(true)}
          onBlur={() => setIsShowKeyBoardHeight(false)}
          onInput={() => setError('')}
          id='login-phone-input'
        />

        <div className={styles.content}>
          <div className={styles.rowPolicy}>
            <Checkbox
              checked={personalDataConsent}
              onChange={() => setPersonalDataConsent(!personalDataConsent)}
            />

            <p className={styles.terms}>
              <span>{'Я предоставляю свое согласие на '}</span>

              <a target='_blank' href={' https://reit-invest.ru/consent'}>
                {'обработку персональных данных.'}
              </a>
            </p>
          </div>

          <div className={styles.rowPolicy}>
            <Checkbox
              checked={privacyPolicyConsent}
              onChange={() => setPrivacyPolicyConsent(!privacyPolicyConsent)}
            />

            <p className={styles.terms}>
              <span>{'Я соглашаюсь с '}</span>

              <a target='_blank' href={'https://reit-invest.ru/privacy_policy'}>
                {'политикой конфиденциальности.'}
              </a>
            </p>
          </div>

          <div className={styles.rowPolicy}>
            <Checkbox
              checked={marketingConsent}
              onChange={() => setMarketingConsent(!marketingConsent)}
            />

            <p className={styles.terms}>
              <span>{'Я даю согласие на '}</span>

              <a target='_blank' href={'https://reit-invest.ru/rek'}>
                {'рекламную рассылку.'}
              </a>
            </p>
          </div>
        </div>
      </div>

      <Button
        loading={isLoading}
        onClick={handleClick}
        label='Отправить код'
        disabled={!canProceed}
      />
    </div>
  );
};

const InputCode: React.FC = () => {
  const authCode = useBoundStore((s) => s.authCode);
  const authPhone = useBoundStore((s) => s.authPhone);

  const personalDataConsent = useBoundStore((s) => s.personalDataConsent);
  const privacyPolicyConsent = useBoundStore((s) => s.privacyPolicyConsent);
  const marketingConsent = useBoundStore((s) => s.marketingConsent);

  const proceedToAuthInputName = useBoundStore((s) => s.proceedToAuthInputName);

  const [code, setCode] = useState(authCode);
  const [resend, setResend] = useState(AUTH_CODE_RESEND_TIMEOUT);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    setResend(AUTH_CODE_RESEND_TIMEOUT);
    await signIn({ phone: authPhone, personalDataConsent, privacyPolicyConsent, marketingConsent });
  };

  const canProceed = Number(code) === Number(authCode.replaceAll(' ', ''));

  useInterval(() => setResend((r) => --r), resend === 0 ? null : 1_000);

  const handleVerifyCode = async () => {
    setIsLoading(true);
    const { result, error } = await verifyAuthCode(authPhone, code);
    setIsLoading(false);

    if (result) {
      proceedToAuthInputName();
    } else if (error) setError(error);
  };

  return (
    <div className={styles.container}>
      <div>
        <p className={styles.desc}>
          {'Код активации придет SMS-сообщением на ваш номер ' + authPhone}
        </p>

        <AuthCodeInput
          autoFocus
          error={error}
          onChange={setCode}
          defaultValue={authCode}
          className={styles.input}
          onInput={() => setError('')}
        />

        <Button
          className={styles.notReceived}
          variant='transparent'
          label={'Переотправить код' + (resend > 0 ? ` (доступно через ${resend}с)` : '')}
          disabled={resend !== 0}
          onClick={handleResend}
        />
      </div>

      <Button
        onClick={handleVerifyCode}
        label='Подтвердить'
        loading={isLoading}
        disabled={code.length !== 6}
      />
    </div>
  );
};

const InputName: React.FC = () => {
  const user = useBoundStore((s) => s.user);
  const closeAuthBottomSheet = useBoundStore((s) => s.closeAuthBottomSheet);

  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const canProceed = name !== '';

  const handleSetupName = async () => {
    setIsLoading(true);
    const { result, error } = await updateUserInfo(name);
    setIsLoading(false);

    if (result) closeAuthBottomSheet();
    else if (error) setError(error);
  };

  return (
    <div className={styles.container}>
      <div>
        <p className={styles.desc}>Ваше имя изменит мир. Как минимум, мир недвижимости</p>

        <TextInput
          tall
          autoFocus
          value={name}
          error={error}
          icon={userIcon}
          onChange={setName}
          placeholder='Олег'
          onInput={() => setError('')}
        />
      </div>

      <Button
        loading={isLoading}
        disabled={!canProceed}
        onClick={handleSetupName}
        label={user ? 'Сохранить' : 'Войти'}
      />
    </div>
  );
};

export default AuthBottomSheet;
